import React, { useState } from 'react';
import './Signup.css';
import { signup } from '../services/authService';
import PracticeAreasModal from './PracticeAreasModal';

const Signup = ({ onBack, onSignupSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mobile_number: '',
    country: '',
    state: '',
    city: '',
    role: 'guest',
    // Lawyer specific fields
    license_no: '',
    consultation_fee: '',
    lawyer_practice: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPracticeModal, setShowPracticeModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setFormData(prev => ({
        ...prev,
        mobile_number: '+92' + value,
      }));
    }
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.mobile_number.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!formData.country.trim()) {
      setError('Country is required');
      return false;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }

    // Lawyer specific validations
    if (formData.role === 'lawyer') {
      if (!formData.license_no.trim()) {
        setError('License number is required for lawyers');
        return false;
      }
      if (!formData.consultation_fee.trim()) {
        setError('Consultation fee is required');
        return false;
      }
      if (!formData.lawyer_practice.trim()) {
        setError('Lawyer practice is required');
        return false;
      }
    }

    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = await signup({
        first_name: formData.first_name,
        last_name: formData.last_name,
        mobile_number: formData.mobile_number,
        role: formData.role,
        ...(formData.role === 'lawyer' && {
          license_no: formData.license_no,
          consultation_fee: formData.consultation_fee,
          lawyer_practice: formData.lawyer_practice,
        }),
      });

      setSuccessMessage(data.message || 'Signup successful!');
      
      // Clear form
      setFormData({
        first_name: '',
        last_name: '',
        mobile_number: '',
        country: '',
        state: '',
        city: '',
        role: 'guest',
        license_no: '',
        consultation_fee: '',
        lawyer_practice: '',
      });

      // Callback for successful signup
      if (onSignupSuccess) {
        setTimeout(() => {
          onSignupSuccess(formData.mobile_number);
        }, 1000);
      }
    } catch (err) {
      console.error('Error:', err);
      
      // Check if mobile number already exists
      if (err.isMobileExists) {
        setError(err.message);
        // Redirect to login after showing error
        setTimeout(() => {
          onBack();
        }, 2000);
      } else {
        setError(err.message || 'Failed to sign up. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-box">
        <div className="signup-brand">INSAFAPP.COM</div>
        <div className="signup-header">
          <div className="header-tabs">
            <button className="tab-button back-button" onClick={onBack} disabled={loading}>
              ← Log in
            </button>
            <button className="tab-button active">Sign up</button>
          </div>
        </div>

        <div className="signup-divider"></div>

        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label className="form-label">
              Please enter your name and phone number details to sign up
            </label>

            {/* First Name */}
            <div className="input-group">
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="First name"
                className={`form-input ${error && !formData.first_name ? 'error' : ''}`}
                disabled={loading}
              />
            </div>

            {/* Last Name */}
            <div className="input-group">
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Last name"
                className={`form-input ${error && !formData.last_name ? 'error' : ''}`}
                disabled={loading}
              />
            </div>

            {/* Phone Number */}
            <div className="input-group phone-group">
              <label className="phone-label">Phone Number</label>
              <div className="phone-input-container">
                <span className="country-code-display">🇵🇰 +92</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={formData.mobile_number.replace('+92', '')}
                  onChange={handlePhoneChange}
                  placeholder="Phone Number"
                  className={`phone-input-field ${error && !formData.mobile_number ? 'error' : ''}`}
                  disabled={loading}
                  maxLength="10"
                />
                <span className="character-count">{formData.mobile_number.replace('+92', '').length}/10</span>
              </div>
            </div>

            {/* Country */}
            <div className="input-group">
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className={`form-select ${error && !formData.country ? 'error' : ''}`}
                disabled={loading}
              >
                <option value="">*Country</option>
                <option value="Pakistan">Pakistan</option>
                <option value="USA">USA</option>
                <option value="UK">UK</option>
                <option value="UAE">UAE</option>
                <option value="India">India</option>
              </select>
            </div>

            {/* State */}
            <div className="input-group">
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`form-select ${error && !formData.state ? 'error' : ''}`}
                disabled={loading}
              >
                <option value="">*State</option>
                <option value="Punjab">Punjab</option>
                <option value="Sindh">Sindh</option>
                <option value="KPK">KPK</option>
                <option value="Balochistan">Balochistan</option>
              </select>
            </div>

            {/* City */}
            <div className="input-group">
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`form-select ${error && !formData.city ? 'error' : ''}`}
                disabled={loading}
              >
                <option value="">*City</option>
                <option value="Karachi">Karachi</option>
                <option value="Lahore">Lahore</option>
                <option value="Islamabad">Islamabad</option>
                <option value="Rawalpindi">Rawalpindi</option>
              </select>
            </div>

            {/* Role Selector */}
            <div className="role-selector">
              <button
                type="button"
                className={`role-button ${formData.role === 'guest' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, role: 'guest' }))}
                disabled={loading}
              >
              Guest Side
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'lawyer' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, role: 'lawyer' }))}
                disabled={loading}
              >
                Lawyer Side
              </button>
            </div>

            {/* Lawyer Specific Fields */}
            {formData.role === 'lawyer' && (
              <>
                <div className="input-group">
                  <input 
                    type="text"
                    name="license_no"
                    value={formData.license_no}
                    onChange={handleInputChange}
                    placeholder="License Number"
                    className={`form-input ${error && !formData.license_no && formData.role === 'lawyer' ? 'error' : ''}`}
                    disabled={loading}
                  />
                </div>

                <div className="input-group">
                  <input
                    type="number"
                    name="consultation_fee"
                    value={formData.consultation_fee}
                    onChange={handleInputChange}
                    placeholder="Consultation Fee"
                    className={`form-input ${error && !formData.consultation_fee && formData.role === 'lawyer' ? 'error' : ''}`}
                    disabled={loading}
                  />
                </div>

                <div className="input-group">
                  <button
                    type="button"
                    onClick={() => setShowPracticeModal(true)}
                    className={`practice-areas-input ${error && !formData.lawyer_practice && formData.role === 'lawyer' ? 'error' : ''}`}
                    disabled={loading}
                  >
                    <span className="practice-areas-icon">📋</span>
                    <span className="practice-areas-text">
                      {formData.lawyer_practice || 'Select Practice Areas'}
                    </span>
                  </button>
                </div>

                <PracticeAreasModal
                  isOpen={showPracticeModal}
                  onClose={() => setShowPracticeModal(false)}
                  onSelect={(areas) => setFormData(prev => ({ ...prev, lawyer_practice: areas }))}
                  selectedAreas={formData.lawyer_practice.split(', ').filter(a => a)}
                />
              </>
            )}

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
          </div>

          <button
            type="submit"
            className={`signup-button ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? 'Requesting OTP...' : 'Request OTP'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Signup;
